import { useNavigate, useLocation } from 'react-router-dom';

import { getMasterclassCasesRoute } from '@/config/routes';
import { useMasterclass } from '@/contexts/MasterclassContext';
import authConfig from '@/modules/auth/config/auth-config.json';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useCaseDetails } from '@/modules/case-selector/hooks/useCaseDetails';
import { getBreadcrumbs } from '@/utils/breadcrumbs';

import { UserProfile, AppHeaderConfig, Breadcrumb } from '../types';

export const useAppHeader = (
  userProfile: UserProfile | null | undefined,
  config: AppHeaderConfig
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCase, masterclassTitle } = useMasterclass();
  const { handleSignOut } = useAuth(authConfig);

  // Get case details to display name instead of ID in breadcrumb
  const { details: caseDetails } = useCaseDetails(selectedCase);
  const caseName = caseDetails?.displayName || null;

  // Generate breadcrumbs based on current route
  const breadcrumbs: Breadcrumb[] = getBreadcrumbs(
    location.pathname,
    selectedCase,
    null, // masterclassId - extracted from pathname inside getBreadcrumbs
    {
      homeLabel: config.breadcrumb?.homeLabel || 'Home',
      caseLabelPrefix: config.breadcrumb?.caseLabelPrefix || 'Case',
      caseName: caseName || undefined,
      masterclassName: masterclassTitle || undefined,
    }
  );

  // Get user initials for avatar fallback
  const getInitials = (): string => {
    if (userProfile?.full_name) {
      return userProfile.full_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return userProfile?.email?.[0]?.toUpperCase() || 'U';
  };

  // Get display name for user menu
  const getDisplayName = (): string => {
    return userProfile?.full_name || userProfile?.email?.split('@')[0] || 'Utilisateur';
  };

  // Navigate to home - context-aware
  // Per CONTEXT.md: "Home icon in case view -> goes to case selector (not home screen)"
  const navigateHome = () => {
    if (!config.logo.clickable) return;

    // Check if we're in a case view (has masterclassId in path)
    // Path pattern: /masterclass/:masterclassId/:caseId or /masterclass/:masterclassId/cases
    const pathParts = location.pathname.split('/');
    const masterclassIndex = pathParts.indexOf('masterclass');

    if (masterclassIndex !== -1 && pathParts[masterclassIndex + 1]) {
      const masterclassId = pathParts[masterclassIndex + 1];
      // If we have a masterclassId, go to case selector for that masterclass
      // But only if we're in a case view (not already in case selector)
      const isInCaseView =
        pathParts.length > masterclassIndex + 2 && pathParts[masterclassIndex + 2] !== 'cases';

      if (isInCaseView) {
        navigate(getMasterclassCasesRoute(masterclassId));
        return;
      }
    }

    // Default: go to home route (masterclass list)
    navigate(config.logo.homeRoute);
  };

  return {
    breadcrumbs,
    displayName: getDisplayName(),
    initials: getInitials(),
    handleSignOut,
    navigateHome,
    navigate,
  };
};
